// Carrier services — map a shop's shipping methods to a carrier's own services.
//
// Why this screen exists: a shipping method with no mapping fails loudly when a
// label is printed, and until now the only way to discover that was an order
// arriving and a packer being stuck. Everything needed to decide is already in
// the WMS — the carrier publishes its catalogue and we sync it — so the setup
// belongs to whoever activates the service in the shop, before the first order,
// not to whoever is asked about it afterwards.
//
// The screen deliberately does NOT guess. The shop and the carrier spell the
// same carrier differently ("HU Fox Post Box" vs "HU FoxPost Box"), so matching
// by name would eventually route a parcel to the wrong carrier. A person picks,
// and the server refuses anything that cannot ship.
import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from './api';

interface Service {
  external_id: string;
  name: string;
  country: string;
  currency: string | null;
  max_weight_kg: number | null;
  has_pickup_points: boolean;
  api_label_allowed: boolean;
  requires_size: boolean;
  requires_phone: boolean;
  requires_email: boolean;
  requires_house_number: boolean;
  disallows_cod: boolean;
}
interface Mapping {
  id: number;
  method_name: string;
  country: string | null;
  service_id: string | null;
  delivery_shape: string;
  service_name: string | null;
  cod_enabled: boolean;
}
interface Method {
  psm_id: number;
  name: string;
  order_count: number;
  countries: string[];
  has_config: boolean;
  carrier_code: string | null;
  mappings: Mapping[];
  /** no_config | needs_mapping | mapped | direct — computed server-side, since
   *  whether a label can be produced depends on the carrier, not on whether a
   *  mapping row happens to exist. */
  status: 'no_config' | 'needs_mapping' | 'mapped' | 'direct';
  blocker: string | null;
}

const SHAPES = [
  { key: 'own_pickup',
    label: "Packeta's own pickup point / Z-BOX",
    hint: 'The point the customer chose is the address. No carrier service needed.' },
  { key: 'carrier_pickup',
    label: 'Carrier pickup point or locker',
    hint: "The carrier's own network — its point id travels separately from the carrier." },
  { key: 'home',
    label: 'Home delivery',
    hint: 'To the door. Street, city and postcode are required on the order.' },
];

export default function CarrierServicesTab({ webshop_id }: { webshop_id: string }) {
  const [methods, setMethods] = useState<Method[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [showMapped, setShowMapped] = useState(false);
  const [form, setForm] = useState({ country: '', service_id: '', delivery_shape: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [m, s] = await Promise.all([
      authFetch(`${API}/shipping/method-mappings?webshop_id=${encodeURIComponent(webshop_id)}`),
      authFetch(`${API}/shipping/carrier-services?carrier_code=packeta`),
    ]);
    if (m.ok) setMethods((await m.json()).methods || []);
    if (s.ok) setServices(await s.json());
    setLoading(false);
  }, [webshop_id]);
  useEffect(() => { load(); }, [load]);

  const countries = Array.from(new Set(services.map(s => s.country))).sort();
  // Only services from the chosen country, and only those whose own nature
  // matches the delivery shape — a locker service cannot be a home delivery.
  const pickable = services.filter(s =>
    s.country === form.country &&
    (form.delivery_shape === 'carrier_pickup' ? s.has_pickup_points
      : form.delivery_shape === 'home' ? !s.has_pickup_points : false));
  const chosen = services.find(s => s.external_id === form.service_id);

  const open = (name: string, countryHint?: string) => {
    setOpenFor(name);
    setError('');
    setForm({ country: countryHint || '', service_id: '', delivery_shape: '' });
  };

  const save = async () => {
    setBusy(true); setError('');
    const r = await authFetch(`${API}/shipping/method-mappings`, {
      method: 'POST',
      body: JSON.stringify({
        webshop_id, carrier_code: 'packeta', method_name: openFor,
        country: form.country,
        service_id: form.delivery_shape === 'own_pickup' ? null : form.service_id,
        delivery_shape: form.delivery_shape,
      }),
    });
    setBusy(false);
    if (!r.ok) { setError((await r.json()).detail || 'Could not save'); return; }
    setOpenFor(null);
    load();
  };

  const remove = async (id: number) => {
    await authFetch(`${API}/shipping/method-mappings/${id}`, { method: 'DELETE' });
    load();
  };

  if (loading) return <div className="text-gray-500 text-sm py-8">Loading…</div>;

  // 'needs setup' is about whether a label can be produced, not whether a
  // mapping row exists: Bring, Posti, DPD and Omniva are direct contracts and
  // never need one. Listing those here would report a problem that is not there.
  const unmapped = methods.filter(m => m.status === 'no_config' || m.status === 'needs_mapping');
  const mapped = methods.filter(m => m.status === 'mapped' || m.status === 'direct');
  const needsAttention = unmapped.filter(m => m.order_count > 0);

  const Row = ({ m }: { m: Method }) => (
    <div className="border-b border-gray-800/60 px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-gray-200 text-sm font-medium truncate">{m.name}</div>
          <div className="text-xs text-gray-600 mt-0.5">
            {m.order_count > 0
              ? <>{m.order_count} order{m.order_count !== 1 ? 's' : ''}
                  {m.countries.length ? ` · ${m.countries.join(', ')}` : ''}</>
              : 'no orders yet'}
            {m.status === 'direct' && m.carrier_code && (
              <span className="text-gray-500"> · {m.carrier_code} (direct contract, no mapping needed)</span>
            )}
          </div>
          {m.blocker && (
            <div className="text-xs text-orange-400 mt-1">{m.blocker}</div>
          )}
          {m.mappings.map(mp => (
            <div key={mp.id} className="mt-1.5 flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-300">{mp.country}</span>
              <span className="text-gray-400">
                {mp.delivery_shape === 'own_pickup'
                  ? "Packeta's own pickup network"
                  : `${mp.service_name || mp.service_id} · ${
                      mp.delivery_shape === 'home' ? 'home delivery' : 'carrier pickup point'}`}
              </span>
              <button onClick={() => remove(mp.id)}
                className="text-gray-600 hover:text-red-400">remove</button>
            </div>
          ))}
        </div>
        {m.status !== 'direct' && (
          <button onClick={() => open(m.name, m.countries[0])}
            className="shrink-0 px-2.5 py-1 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white">
            {m.mappings.length ? 'Add country' : 'Map'}
          </button>
        )}
      </div>

      {openFor === m.name && (
        <div className="mt-3 bg-[#12141c] rounded-lg p-3 space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Destination country</label>
            <select value={form.country}
              onChange={e => setForm(f => ({ ...f, country: e.target.value, service_id: '' }))}
              className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded px-2 py-1.5 w-48">
              <option value="">Choose…</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              The same method can mean a different carrier in each market, so each country is mapped separately.
            </p>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">How is it delivered?</label>
            <div className="space-y-1">
              {SHAPES.map(s => (
                <label key={s.key} className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="shape" checked={form.delivery_shape === s.key}
                    onChange={() => setForm(f => ({ ...f, delivery_shape: s.key, service_id: '' }))}
                    className="mt-1 accent-blue-500" />
                  <span>
                    <span className="text-sm text-gray-300">{s.label}</span>
                    <span className="block text-xs text-gray-600">{s.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {form.delivery_shape && form.delivery_shape !== 'own_pickup' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Which Packeta service? {form.country && `(${pickable.length} available in ${form.country})`}
              </label>
              <select value={form.service_id} disabled={!form.country}
                onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))}
                className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded px-2 py-1.5 w-full max-w-md disabled:opacity-40">
                <option value="">{form.country ? 'Choose…' : 'Choose a country first'}</option>
                {pickable.map(s => (
                  <option key={s.external_id} value={s.external_id}>{s.name}</option>
                ))}
              </select>
              {chosen && (
                <p className="text-xs text-gray-500 mt-1.5">
                  {chosen.disallows_cod
                    ? <span className="text-orange-400">Does not accept cash on delivery. </span>
                    : <>Settles cash on delivery in {chosen.currency}. </>}
                  {chosen.requires_phone && 'Needs a phone number. '}
                  {chosen.requires_house_number && 'Needs the house number separately. '}
                  {chosen.requires_size && 'Needs parcel dimensions. '}
                  {chosen.max_weight_kg && `Max ${chosen.max_weight_kg} kg. `}
                  {!chosen.api_label_allowed && 'No direct carrier label — a Packeta label is used.'}
                </p>
              )}
            </div>
          )}

          {error && <div className="text-xs text-red-400 bg-red-900/20 rounded px-2 py-1.5">{error}</div>}

          <div className="flex gap-2">
            <button onClick={save} disabled={busy || !form.country || !form.delivery_shape ||
                     (form.delivery_shape !== 'own_pickup' && !form.service_id)}
              className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
              {busy ? 'Saving…' : 'Save mapping'}
            </button>
            <button onClick={() => setOpenFor(null)}
              className="px-3 py-1.5 text-xs rounded bg-gray-800 hover:bg-gray-700 text-gray-300">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800">
        <div className="px-4 py-3 border-b border-gray-800">
          <h3 className="text-sm font-semibold text-white">Needs setting up</h3>
          <p className="text-xs text-gray-600 mt-0.5">
            These shipping methods cannot print a label yet. A resold carrier is mapped to one
            of its services per destination country — nothing is matched by name, because the
            shop and the carrier spell the same carrier differently. Direct contracts (Bring,
            Posti, DPD, Omniva) need no mapping and are not listed here.
          </p>
        </div>
        {unmapped.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-600 text-sm">
            Every shipping method is mapped.
          </div>
        ) : (
          <>
            {needsAttention.length > 0 && (
              <div className="px-4 py-2 bg-orange-900/20 text-orange-300 text-xs">
                {needsAttention.length} of these already {needsAttention.length === 1 ? 'has' : 'have'} orders —
                those orders cannot be labelled until mapped.
              </div>
            )}
            {unmapped.map(m => <Row key={m.psm_id} m={m} />)}
          </>
        )}
      </div>

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800">
        <button onClick={() => setShowMapped(v => !v)}
          className="w-full px-4 py-3 flex items-center justify-between text-left">
          <div>
            <h3 className="text-sm font-semibold text-white">Ready ({mapped.length})</h3>
            <p className="text-xs text-gray-600 mt-0.5">Methods that can print a label today</p>
          </div>
          <span className="text-gray-500 text-xs">{showMapped ? 'hide' : 'show'}</span>
        </button>
        {showMapped && mapped.map(m => <Row key={m.psm_id} m={m} />)}
      </div>

      <PacketaInsuranceCard />
    </div>
  );
}

// Packeta insurance (Fernando, 2026-10-13): every parcel declares its service's maximum FREE insurance
// amount, from Packeta's price list. A service without a free tier declares 1, as every parcel did before.
interface InsuranceRow {
  service_key: string; country: string; service_name: string; service_currency: string | null;
  free_amount: number | null; currency: string | null; max_amount: number | null; max_currency: string | null;
  source: string | null; updated_by: string | null; updated_at: string | null;
}

function PacketaInsuranceCard() {
  const [rows, setRows] = useState<InsuranceRow[]>([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<{ key: string; free: string; cur: string; max: string } | null>(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    const r = await authFetch(`${API}/shipping/packeta-insurance`);
    if (r.ok) setRows((await r.json()).services || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!edit) return;
    const r = await authFetch(`${API}/shipping/packeta-insurance/${encodeURIComponent(edit.key)}`, {
      method: 'PUT', body: JSON.stringify({ free_amount: edit.free, currency: edit.cur, max_amount: edit.max, max_currency: edit.cur }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || `Could not save (HTTP ${r.status})`); return; }
    setEdit(null); setMsg(''); load();
  };
  const missing = rows.filter(r => r.free_amount == null).length;
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800">
      <button onClick={() => setOpen(v => !v)} className="w-full px-4 py-3 flex items-center justify-between text-left">
        <div>
          <h3 className="text-sm font-semibold text-white">Packeta insurance ({rows.length} services)</h3>
          <p className="text-xs text-gray-600 mt-0.5">
            Every parcel is insured for its service's maximum free amount (Packeta price list). {missing > 0 &&
              `${missing} without a free tier declare 1.`}
          </p>
        </div>
        <span className="text-gray-500 text-xs">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="px-4 pb-3">
          {msg && <div className="text-xs text-red-400 mb-2">{msg}</div>}
          <table className="w-full text-xs">
            <thead><tr className="text-gray-500 text-left">
              <th className="py-1 pr-2">Service</th><th className="py-1 pr-2">Free up to</th>
              <th className="py-1 pr-2">Maximum</th><th className="py-1 pr-2">Source</th><th></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.service_key} className="border-t border-gray-800 text-gray-300">
                  <td className="py-1 pr-2">{r.service_name}</td>
                  {edit?.key === r.service_key ? (
                    <td className="py-1 pr-2" colSpan={2}>
                      <input value={edit.free} onChange={e => setEdit({ ...edit, free: e.target.value })} placeholder="free up to"
                        className="keep-width w-24 px-1 py-0.5 rounded bg-[#0f1117] border border-gray-700 text-white" />{' '}
                      <input value={edit.cur} onChange={e => setEdit({ ...edit, cur: e.target.value })} placeholder="CUR"
                        className="keep-width w-12 px-1 py-0.5 rounded bg-[#0f1117] border border-gray-700 text-white" />{' '}
                      max <input value={edit.max} onChange={e => setEdit({ ...edit, max: e.target.value })}
                        className="keep-width w-20 px-1 py-0.5 rounded bg-[#0f1117] border border-gray-700 text-white" />
                    </td>
                  ) : (<>
                    <td className="py-1 pr-2">{r.free_amount != null ? `${Number(r.free_amount).toLocaleString()} ${r.currency}` : <span className="text-gray-500">no free tier — 1</span>}</td>
                    <td className="py-1 pr-2 text-gray-500">{r.max_amount != null ? `${Number(r.max_amount).toLocaleString()} ${r.max_currency || r.currency}` : '—'}</td>
                  </>)}
                  <td className="py-1 pr-2 text-gray-600">{r.source || 'no row yet'}</td>
                  <td className="py-1 text-right whitespace-nowrap">
                    {edit?.key === r.service_key ? (<>
                      <button onClick={save} className="text-blue-400 hover:text-blue-300 mr-2">save</button>
                      <button onClick={() => setEdit(null)} className="text-gray-500">cancel</button>
                    </>) : (
                      <button onClick={() => setEdit({ key: r.service_key, free: r.free_amount != null ? String(r.free_amount) : '',
                        cur: r.currency || r.service_currency || '', max: r.max_amount != null ? String(r.max_amount) : '' })}
                        className="text-gray-500 hover:text-gray-300">edit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
